/**
 * Expanded stage detail table — shown when a file row is expanded.
 */

import { CheckCircle, Clock, AlertCircle, Loader2, HelpCircle } from 'lucide-react'
import { cn, formatDate, formatNumber } from '@/lib/utils'
import type { PipelineStageStatus } from '../types'

const STAGE_LABELS: Record<string, string> = {
  file_registered: 'File Registered',
  raw_published: 'Raw Published',
  fhid_assigned: 'FHID Assigned',
  mongo_upserted: 'Mongo Upserted',
  cdc_emitted: 'CDC Emitted',
}

const STATUS_CONFIG = {
  done:        { label: 'Done',        dot: 'bg-green-500',  text: 'text-green-700',  bg: 'bg-green-50'  },
  in_progress: { label: 'In Progress', dot: 'bg-blue-500',   text: 'text-blue-700',   bg: 'bg-blue-50'   },
  pending:     { label: 'Pending',     dot: 'bg-gray-400',   text: 'text-gray-500',   bg: 'bg-gray-50'   },
  error:       { label: 'Error',       dot: 'bg-red-500',    text: 'text-red-700',    bg: 'bg-red-50'    },
  unknown:     { label: 'Unknown',     dot: 'bg-gray-300',   text: 'text-gray-400',   bg: 'bg-gray-50'   },
}

function StatusBadge({ status }: { status: PipelineStageStatus['status'] }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.unknown
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full', cfg.bg, cfg.text)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />
      {cfg.label}
    </span>
  )
}

function StageIcon({ status }: { status: PipelineStageStatus['status'] }) {
  const cls = 'h-4 w-4 flex-shrink-0'
  switch (status) {
    case 'done':        return <CheckCircle className={cn(cls, 'text-green-600')} />
    case 'in_progress': return <Loader2 className={cn(cls, 'text-blue-500 animate-spin')} />
    case 'pending':     return <Clock className={cn(cls, 'text-gray-400')} />
    case 'error':       return <AlertCircle className={cn(cls, 'text-red-500')} />
    default:            return <HelpCircle className={cn(cls, 'text-gray-300')} />
  }
}

interface Props {
  stages: PipelineStageStatus[]
  bottleneck: string | null
}

export function PipelineStageDetail({ stages, bottleneck }: Props) {
  return (
    <div className="bg-gray-50 border-t border-gray-100 px-6 py-4">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-400 font-medium uppercase tracking-wide">
              <th className="text-left pb-2 pr-4">Stage</th>
              <th className="text-left pb-2 pr-4">Status</th>
              <th className="text-right pb-2 pr-4">Expected</th>
              <th className="text-right pb-2 pr-4">Actual</th>
              <th className="text-right pb-2 pr-4">Lag</th>
              <th className="pb-2 pr-4 w-32">Progress</th>
              <th className="text-left pb-2 pr-4">Last Activity</th>
              <th className="text-left pb-2">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {stages.map((stage) => {
              const isBottleneck = stage.stage === bottleneck
              return (
                <tr
                  key={stage.stage}
                  className={cn('align-middle', isBottleneck && 'bg-amber-50')}
                >
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-2">
                      <StageIcon status={stage.status} />
                      <span className={cn('font-medium', isBottleneck ? 'text-amber-800' : 'text-gray-800')}>
                        {STAGE_LABELS[stage.stage] ?? stage.stage}
                      </span>
                      {isBottleneck && (
                        <span className="text-[10px] bg-amber-100 text-amber-700 font-semibold px-1.5 py-0.5 rounded">
                          bottleneck
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-4">
                    <StatusBadge status={stage.status} />
                  </td>
                  <td className="py-2 pr-4 text-right text-gray-600 font-mono text-xs">
                    {formatNumber(stage.expected)}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-xs">
                    {stage.actual != null ? (
                      <span className={stage.actual >= stage.expected ? 'text-green-700' : 'text-blue-700'}>
                        {formatNumber(stage.actual)}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-xs">
                    {stage.lag != null && stage.lag > 0 ? (
                      <span className="text-orange-600">{formatNumber(stage.lag)}</span>
                    ) : stage.lag === 0 ? (
                      <span className="text-green-600">0</span>
                    ) : '—'}
                  </td>
                  <td className="py-2 pr-4 w-32">
                    {stage.pctComplete != null ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={cn(
                              'h-full rounded-full transition-all',
                              stage.pctComplete >= 100 ? 'bg-green-500' :
                              stage.pctComplete > 0   ? 'bg-blue-500'  : 'bg-gray-300'
                            )}
                            style={{ width: `${Math.min(100, stage.pctComplete)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 w-8 text-right">
                          {stage.pctComplete}%
                        </span>
                      </div>
                    ) : '—'}
                  </td>
                  <td className="py-2 pr-4 text-xs text-gray-400">
                    {stage.lastActivity ? formatDate(stage.lastActivity) : '—'}
                  </td>
                  <td className="py-2 text-xs text-gray-400 max-w-[240px]">
                    {stage.details ? (
                      <div className="truncate" title={JSON.stringify(stage.details)}>
                        {Object.entries(stage.details)
                          .filter(([k]) => !['start_offset_at_upload', 'end_offset', 'committed_offset'].includes(k))
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(' · ')}
                      </div>
                    ) : '—'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
